import * as React from 'react';
import { pickBy } from 'lodash-es';
import { Redirect, useLocation } from 'react-router-dom';
import * as querystring from 'query-string';
import { ShoppingCartContextProvider, CartType, Footer, HotelCard } from '@weddingspot/ws-core-components';
import { PageAlert } from '@cvent/carina/components/Alert';
import { Pagination } from '@cvent/carina/components/Pagination';

import GooglePlacesAutocomplete from './Autocomplete';
import { SearchContext, SearchLocation } from './SearchContext';
import { Coords } from 'google-map-react';
import { HotelsMap } from './HotelsMap';
import { HotelShoppingCart } from './HotelShoppingCart';
import { HotelRFPContainer } from './HotelRFPContainer';
import { logEvent, AnalyticsEvent } from '@weddingspot/ws-analytics';
import { FilterBar, FormikValues } from './FilterBar';
import {
    CSNAmenity,
    CSNAreaType,
    CSNGeographicPointFilter,
    CSNBoundingBox,
    CSNVenueSearchInput,
    CSNServiceApi,
} from '@weddingspot/ws-api-client';
import { Base64 } from 'js-base64';
import cn from 'classnames';
import { Helmet } from 'react-helmet';
import { useDeepCompareMemoize } from '../search-result-page/hooks';
import useQueryStringVal from '../../hooks/userQueryString';
import { APP_ROOT_DOM_NODE_ID } from '../../constants/config';
import AppLogger from '../../utils/logging';
const logger = AppLogger.createLogger();

const styles = require('./index.module.scss');

interface LocationState {
    // Search location string
    q?: string;
    f?: CSNGeographicPointFilter;
    b?: CSNBoundingBox;
    // Room count
    r_min?: number;
    r_max?: number;
    am?: string[];
    ar?: string[];
}

const HotelsSearch = () => {
    const location = useLocation<LocationState>();
    const qs = querystring.parse(location.search);
    let parsedState = '{}';
    if (qs.state) {
        parsedState = Base64.atob(qs.state as string);
    }
    const locationState = location.state || JSON.parse(parsedState) || {};

    const [searchLocation, setSearchLocation] = React.useState<SearchLocation>({
        query: locationState.q,
        geoFilter: locationState.f,
        boundingBox: locationState.b,
    });
    const [queryLocation, setQueryLocation] = React.useState<Coords>();

    type RoomState = [number | null, number | null] | undefined;
    const [rooms, setRooms] = React.useState<RoomState>([locationState.r_min || null, null]);
    const [amenities, setAmenities] = React.useState<CSNAmenity[]>();
    const [areas, setAreas] = React.useState<CSNAreaType[]>();
    const [radius, setRadius] = React.useState(60);

    const [autocompliteActive, setAutocompliteActive] = React.useState(false);

    const [loading, setLoading] = React.useState(false);
    const [searchResults, setSearchResults] = React.useState<any[]>([]);
    const [profilesResults, setProfilesResults] = React.useState<any[]>([]);
    const [searchInUse, setSearchInUse] = React.useState<boolean>(false);

    const [quickViewPadding, setQuickViewPadding] = React.useState(0);
    const [focusedHotel, setFocusedHotel] = React.useState<string | undefined>();
    const listRef = React.useRef<HTMLDivElement>(null);
    const refMap = React.useRef<Record<string, HTMLDivElement | null>>({});

    // Pagination state vars
    const [page, setPage] = useQueryStringVal('page');
    const currentPage = parseInt(page as string, 10) || 1;
    const [totalPages, setTotalPages] = React.useState<number>(1);
    const itemsPerPage = 10;

    const [rfpModalShowing, setRfpModalShowing] = React.useState<boolean>(false);

    React.useEffect(
        () => {
            if (searchLocation.geoFilter) {
                setQueryLocation(
                    {
                        lat: searchLocation.geoFilter.geographicPoint.lat,
                        lng: searchLocation.geoFilter.geographicPoint.lon
                    });
            }
        },
        [searchLocation]);

    React.useEffect(() => {
        const params = qs.state ? { Source: 'Venue Profile' } : {};
        logEvent(AnalyticsEvent.EVTNM_VISITED_HOTELS_SRP, params);
    }, []);

    React.useEffect(() => {
        if (!focusedHotel) {
            setQuickViewPadding(0);
        }
    }, [focusedHotel]);

    React.useEffect(() => {
        const globalClickListener = (e: MouseEvent) => {
            const el = e.target as Node;
            if (!el || !focusedHotel) {
                return;
            }

            const container = document.querySelector(`.index-module--bodyContainer`);
            // We handle click outside only INSIDE map and hotel list.
            // For example if user click to search box we dont need unselect hotel
            if (!container || !container.contains(el)) {
                return;
            }

            const activePin = document.querySelector(`[data-annotation-id="${focusedHotel}"]`);
            const hotelCard = document.querySelector(`[data-hotel-list] [data-id="${focusedHotel}"]`);

            const pinClicked = activePin && activePin.contains(el);
            const venueClicked = hotelCard && hotelCard.contains(el);

            if (!pinClicked && !venueClicked) {
                setFocusedHotel(undefined);
            }
        };
        document.addEventListener('click', globalClickListener);
        return () => document.removeEventListener('click', globalClickListener);
    }, [focusedHotel]);

    const onPlaceSelected = (address: string, geoFilter: CSNGeographicPointFilter, boundingBox: CSNBoundingBox) => {
        setSearchLocation({
            query: address,
            geoFilter: geoFilter,
            boundingBox: boundingBox,
        });

        setPage(undefined);

        logEvent(AnalyticsEvent.EVTNM_CLICKED_HOTEL_LOCATIONS, { source: 'hotel blocks SRP', location: address });
    };

    const performRequest = () => {
        if (!(searchLocation.query && searchLocation.geoFilter)) {
            return;
        }
        setLoading(true);
        setFocusedHotel(undefined);
        const requestPayload: CSNVenueSearchInput = {
            query: searchLocation.query,
            geoFilter: searchLocation.geoFilter,
            boundingBox: searchLocation.boundingBox,
            radius: radius,
            pageFilter: {
                from: (currentPage - 1) * itemsPerPage,
                size: itemsPerPage,
            },
        };

        if (rooms && (rooms[0] || rooms[1])) {
            requestPayload.guestRoomsFilter = {
                min: rooms[0] || null,
                max: null,
            };
        }
        if (amenities && amenities.length) {
            requestPayload.amenitiesFilter = { amenities };
        }
        if (areas && areas.length) {
            requestPayload.areasFilter = { areas };
        }

        (async () => {
            const searchRes = await CSNServiceApi.venuesSearch(requestPayload);
            const profilesRes = await CSNServiceApi.getVenueProfiles({ venueIds: searchRes.searchResult.venueList.map((v) => v.id) });
            setSearchResults(searchRes.searchResult.venueList);
            setTotalPages(Number(((searchRes.searchResult.totalVenueHits || 0) / itemsPerPage).toFixed()));
            setProfilesResults(profilesRes);
            setTimeout(() => {
                if (listRef.current) {
                    listRef.current.scroll({ top: 0, behavior: 'smooth' });
                }
            }, 0);
        })()
            .catch((err) => {
                logger.warn(err);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    React.useEffect(() => {
        performRequest();
    }, [
        useDeepCompareMemoize(searchLocation),
        useDeepCompareMemoize(rooms),
        useDeepCompareMemoize(amenities),
        useDeepCompareMemoize(areas),
        radius,
        currentPage,
    ]);

    const onFilterSubmit = (values: FormikValues) => {
        const rooms = values.rooms || [null, null];
        const amenities = Object.keys(pickBy(values.amenities)) as CSNAmenity[];
        const areas = Object.keys(pickBy(values.areas)) as CSNAreaType[];

        setRooms(rooms);
        setAmenities(amenities);
        setAreas(areas);
        setRadius(values.radius);
        setPage(undefined);
    };

    const onClickShoppingCartSubmit = () => {
        setRfpModalShowing(true);
        logEvent(AnalyticsEvent.EVTNM_CLICKED_HOTEL_CART_REQUEST_PROPOSAL, { source: 'hotel block SRP' });
    };

    const onClear = () => {
        setSearchInUse(true);
        setSearchLocation({});
    };

    const innerSticky = (
        <>
            <div className={styles.autocomplete}>
                <GooglePlacesAutocomplete
                    isActive={autocompliteActive}
                    onChangeActive={(isActive) => setAutocompliteActive(isActive)}
                    onPlaceSelected={onPlaceSelected}
                    onClear={onClear}
                    defaultAddress={searchLocation.query}
                />
            </div>
            <FilterBar onFilterSubmit={onFilterSubmit} roomsMin={rooms ? rooms[0] : null} roomsMax={rooms ? rooms[1] : null} />
            <div>
                <HotelShoppingCart onClickSubmit={onClickShoppingCartSubmit} />
            </div>
        </>
    );

    const searchContextValues = {
        query: searchLocation.query,
        queryLocation,
        setSearchLocation,
        searchResults,
        setSearchResults,
    };

    const mapContainerRef = React.useRef<HTMLDivElement>(null);

    if (!searchLocation.query && !searchInUse) {
        return <Redirect to='/hotels/' />;
    }

    return (
        <>
            <Helmet meta={[{ name: 'robots', content: 'noindex, nofollow' }]}></Helmet>
            <ShoppingCartContextProvider portalId={APP_ROOT_DOM_NODE_ID} cartType={CartType.HOTELS} itemsMax={10}>
                <SearchContext.Provider value={searchContextValues}>
                    <div className={styles.pageContainer}>
                        <div className={styles.titleContainer}>
                            <div className={cn({ [styles.isAutocompliteActive]: autocompliteActive }, styles.header)}>{innerSticky}</div>
                        </div>
                        <div className={styles.bodyContainer}>
                            <div className={styles.resultContainer} ref={listRef}>
                                <div>
                                    <h1 className={styles.title}>Hotel Room Search</h1>
                                    <div className={styles.subtitle}>
                                        Enter the name or address of your wedding venue to find the best hotels that are nearby.
                                    </div>
                                </div>
                                <div
                                    data-hotel-list={true}
                                    className={cn({ [styles.focusMode]: focusedHotel }, styles.listContainer)}
                                    style={{ paddingTop: Math.max(0, quickViewPadding) + 16 + 'px' }}
                                >
                                    {loading ? (
                                        'Loading...'
                                    ) : (
                                        <>
                                            {searchResults.length
                                                ? searchResults.map((v, i) => (
                                                      <HotelCard
                                                          onQuickView={(height) => setQuickViewPadding(height - 215)}
                                                          onRefSet={(element) => (refMap.current[v.id] = element)}
                                                          key={i}
                                                          id={v.id}
                                                          profileData={profilesResults.find((p) => p.basicProfile.id === v.id)}
                                                          name={v.name}
                                                          hotelType={v.type}
                                                          state={v.stateProvinceCode}
                                                          city={v.city}
                                                          guestRooms={v.totalSleepingRoom}
                                                          rating={v.preferredRating}
                                                          imageUrl={v.heroImage}
                                                          eventCoordinates={
                                                              queryLocation ? [queryLocation.lat, queryLocation.lng] : undefined
                                                          }
                                                          hotelCoordinates={
                                                              v.geographicLocation && [v.geographicLocation.lat, v.geographicLocation.lon]
                                                          }
                                                          focused={focusedHotel === v.id}
                                                      />
                                                  ))
                                                : queryLocation && (
                                                      <div className={styles.noResults}>
                                                            <PageAlert
                                                                id="1"
                                                                appearance="info"
                                                                content= {
                                                                    <span>
                                                                        <div className={styles.infoAlertTitle}>No results.</div>
                                                                        <div className={styles.infoAlertDescription}>Consider adjusting your filters (location, spelling, or other search criteria).</div>
                                                                    </span>
                                                                }
                                                            />
                                                      </div>
                                                  )}
                                        </>
                                    )}
                                </div>
                                {totalPages > 1 && (
                                    <div className={styles.pagination}>
                                        {searchLocation.query && queryLocation && searchResults ? (
                                            <Pagination
                                                totalPages={totalPages}
                                                defaultSelected={1}
                                                selected={currentPage}
                                                onChange={setPage as ((val: any) => void)}
                                            />
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                )}
                                <Footer />
                            </div>
                            <div className={styles.mapContainer} ref={mapContainerRef}>
                                <HotelsMap
                                    isLoading={loading}
                                    hotels={searchResults}
                                    searchLocation={queryLocation}
                                    focusedHotel={focusedHotel}
                                    onMarkerClick={(id) => {
                                        const element = refMap.current[id] as HTMLDivElement;
                                        if (!element) {
                                            return;
                                        }

                                        if (focusedHotel === id) {
                                            setFocusedHotel(undefined);
                                        } else {
                                            setFocusedHotel(id);
                                            if (listRef.current && window.innerWidth > 750) {
                                                listRef.current.scroll({
                                                    top: element.offsetTop,
                                                    behavior: 'smooth',
                                                });
                                            }
                                            if (mapContainerRef.current && window.innerWidth < 750) {
                                                mapContainerRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <HotelRFPContainer rfpModalShowing={rfpModalShowing} setRfpModalShowing={setRfpModalShowing} />
                </SearchContext.Provider>
            </ShoppingCartContextProvider>
        </>
    );
};

export default HotelsSearch;
